import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { isType } from '@sorare/core/src/lib/gql';
import { useCommonGameSet } from '@sorare/core/src/set';

import type { SetCollectionTask_taskInterface } from '#src/components/missions/SetCollectionTask/__generated__/index.graphql.ts';
import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import type { SetCollectionTaskPreview_taskInterface } from './__generated__/index.graphql.ts';

const Container = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-200);
  position: relative;
  overflow: hidden;
`;

const ContentSection = styled(Vertical).attrs({ gap: 1 })`
  flex: 1;
  min-width: 0;
`;

const ProgressSection = styled(Horizontal).attrs({ gap: 1 })`
  align-items: center;
  width: 100%;
`;

const StyledProgress = styled(Progress)`
  flex: 1;
  min-width: 0;
`;

const ProgressText = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: center;
  flex-shrink: 0;
`;

const ProgressDivider = styled(LabelS)`
  color: var(--c-nd-600);
`;

const ProgressIcon = styled.div`
  flex-shrink: 0;
`;

type Props = {
  task:
    | SetCollectionTaskPreview_taskInterface
    | SetCollectionTask_taskInterface;
  overrideDescription?: ReactNode;
};

export const SetCollectionTaskPreview = ({
  task,
  overrideDescription,
}: Props) => {
  const commonGameSet = useCommonGameSet();

  if (
    !isType(task, 'CardsCountTask') &&
    !isType(task, 'CompleteCollectionsCountTask')
  ) {
    return null;
  }

  const competitionColor = getTaskCompetitionColor(
    task,
    'var(--c-set-surface-primary)'
  );
  const { rewardConfigs, liveScore, scoreToReach = 1, description } = task;
  const rewardConfig = rewardConfigs?.at(0);

  return (
    <Container>
      {!!rewardConfig && (
        <AnyRewardConfig
          anyRewardConfig={rewardConfig}
          variant={Variant.CHIP}
          textVariantSize="lg"
        />
      )}
      <ContentSection>
        {typeof overrideDescription !== 'undefined' ? (
          overrideDescription
        ) : (
          <BodyS>{description}</BodyS>
        )}
        <ProgressSection>
          <StyledProgress
            value={liveScore}
            max={scoreToReach}
            color={competitionColor}
            bgColor="var(--c-nd-700)"
          />
          <ProgressText>
            <BodyS bold>{liveScore}</BodyS>
            <ProgressDivider>/</ProgressDivider>
            <BodyS bold>{scoreToReach}</BodyS>
            <ProgressIcon>
              {isType(task, 'CardsCountTask') ? (
                <ScarcityIcon
                  rarity={Rarity.common}
                  cardSet={commonGameSet.slug}
                />
              ) : (
                <Collection size={2} />
              )}
            </ProgressIcon>
          </ProgressText>
        </ProgressSection>
      </ContentSection>
    </Container>
  );
};

SetCollectionTaskPreview.fragments = {
  taskInterface: gql`
    fragment SetCollectionTaskPreview_taskInterface on TaskInterface {
      id
      description
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        scoreToReach
      }
      ... on CardsCountTask {
        id
        liveScore
        scoreToReach
      }
      ...getTaskCompetitionColor_taskInterface
    }
    ${getTaskCompetitionColor.fragments.taskInterface}
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<SetCollectionTaskPreview_taskInterface>,
};
